import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { isPlatformRole, type OrgUserRole } from "@/types/firestore";
import type { PlatformMaintenanceSettings, OrgMaintenance, MaintenanceStatus } from "@/types/maintenance";

const DEFAULT_CONTACT = "studio@bigimpactgroup.ai";

/**
 * Real-time listener for maintenance mode status.
 * Returns whether the current user is blocked by maintenance,
 * plus admin-facing info (platformActive, orgActive) for banners.
 */
export function useMaintenanceStatus(
  role: OrgUserRole | null,
  orgId: string | null | undefined
): MaintenanceStatus {
  const [platformSettings, setPlatformSettings] = useState<PlatformMaintenanceSettings | null>(null);
  const [orgMaintenance, setOrgMaintenance] = useState<OrgMaintenance | null>(null);
  const [orgName, setOrgName] = useState<string | null>(null);
  const [platformLoading, setPlatformLoading] = useState(true);
  const [orgLoading, setOrgLoading] = useState(!!orgId);

  // Listen to platformSettings/maintenance
  useEffect(() => {
    if (!role || role === "ORG_REQUESTER") {
      setPlatformSettings(null);
      setPlatformLoading(false);
      return;
    }
    const ref = doc(db, "platformSettings", "maintenance");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          setPlatformSettings(snap.data() as PlatformMaintenanceSettings);
        } else {
          setPlatformSettings(null);
        }
        setPlatformLoading(false);
      },
      () => {
        // If read fails (e.g. unauthenticated), treat as no maintenance
        setPlatformSettings(null);
        setPlatformLoading(false);
      }
    );
    return unsub;
  }, [role]);

  // Listen to org doc for org-level maintenance
  useEffect(() => {
    if (!orgId || role === "ORG_REQUESTER") {
      setOrgMaintenance(null);
      setOrgName(null);
      setOrgLoading(false);
      return;
    }

    setOrgLoading(true);
    const ref = doc(db, "orgs", orgId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setOrgMaintenance(data.maintenance ?? null);
          setOrgName(data.name ?? null);
        } else {
          setOrgMaintenance(null);
          setOrgName(null);
        }
        setOrgLoading(false);
      },
      () => {
        setOrgMaintenance(null);
        setOrgName(null);
        setOrgLoading(false);
      }
    );
    return unsub;
  }, [orgId, role]);

  const loading = platformLoading || orgLoading;

  const platformActive = platformSettings?.isActive === true;
  const orgActive = orgMaintenance?.isActive === true;

  // Determine if this user is blocked
  const isPlatform = role ? isPlatformRole(role) : false;
  const isOrgAdmin = role === "ORG_OWNER" || role === "ORG_ADMIN";

  let blocked = false;
  let scope: "platform" | "org" | null = null;
  let message: string | null = null;
  let estimatedEnd = null;
  let contactEmail = DEFAULT_CONTACT;

  if (platformActive && !isPlatform) {
    // Platform maintenance blocks everyone except platform roles
    blocked = true;
    scope = "platform";
    message = platformSettings?.message ?? null;
    estimatedEnd = platformSettings?.estimatedEnd ?? null;
    contactEmail = platformSettings?.contactEmail ?? DEFAULT_CONTACT;
  } else if (orgActive && !isPlatform && !isOrgAdmin) {
    // Org maintenance blocks non-admin org users
    // (ORG_OWNER/ORG_ADMIN see through their own org's maintenance)
    blocked = true;
    scope = "org";
    message = orgMaintenance?.message ?? null;
    estimatedEnd = orgMaintenance?.estimatedEnd ?? null;
  }

  return {
    loading,
    blocked,
    scope,
    message,
    estimatedEnd,
    contactEmail,
    orgName,
    platformActive,
    orgActive,
  };
}
